import { Injectable, inject, signal } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { Capacitor } from '@capacitor/core';
import { Dialog } from '@capacitor/dialog';
import { ModalController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { NewClientDialogComponent } from '../clients/new-client/new-client-modal.dialog';
import { SelectSiteDialog } from '../components/select-site/select-site.dialog';
import { SalesRoundDialogComponent } from '../contractors/sales/sales-round/sales-round.dialog';
import { InstructionPopover } from '../instructions/new/popover/instruction.popover';
import { FreeSelectWorkerDialogComponent } from '../kanban/ticket/component/select-worker/dialog/select-worker.dialog';
import { NewMaterialDialog } from '../materials/new-material/new-material.dialog';
import { Attachment, IAttachment } from '../models/attachment.model';
import { Client } from '../models/client.model';
import { ListItem } from '../models/list-item.model';
import { Note } from '../models/note.model';
import { PartnerNote } from '../models/partner-note.model';
import { Room } from '../models/room.model';
import { SiteArea } from '../models/site-area.model';
import { SiteProjectPeriod } from '../models/site-project-period.model';
import { SiteProject } from '../models/site-project.model';
import { SiteReport, SiteReportTicket } from '../models/site-report.model';
import { Site } from '../models/site.model';
import { RoomTicket } from '../models/ticket.model';
import { User } from '../models/user.model';
import { NewNoteDialogComponent } from '../notes/dialog/new-note.dialog';
import { PartnerNoteDialogComponent } from '../partner/notes/dialog/partner-note.dialog';
import { SiteContractDialogComponent } from '../site-contract/contract-dialog/contract.dialog';
import { NewSiteDialogComponent } from '../sites/new-site/new-site.dialog';
import { AddAttachmentDialogComponent } from '../sites/site/components/attachments/dialog/add-attachment.dialog';
import { NewSiteReportDialogComponent } from '../sites/site/components/reports/new/new-site-report.dialog';
import { AddAreaDialogComponent } from '../sites/site/components/rooms/components/add-area/add-area.dialog';
import { SiteTimesheetDialogComponent } from '../sites/site/components/timesheet/timesheet.dialog';
import { NewTimesheetDialogComponent } from '../timesheets/new-timesheet/new-timesheet.dialog';
import { InviteUserDialogComponent } from '../users/invite/invite-user.dialog';
import { Roles } from '../utility/role';
import { WorkMachineDialogComponent } from '../work-machines/dialog/work-machine.dialog';
import { ClientsService } from './clients.service';
import { ContractorsService } from './contractors.service';
import { InitializeService } from './initialize.service';
import { LicenseService } from './licence.service';
import { MaterialsService } from './materials.service';
import { NavigationService } from './navigation.service';
import { NotesService } from './notes.service';
import { PartnerNotesService } from './partner-notes.service';
import { SchedulesService } from './schedules.service';
import { SiteAreasService } from './site-areas.service';
import { SiteProjectsService } from './site-projects.service';
import { SiteReportsService } from './site-report.service';
import { SitesService } from './sites.service';
import { TimesheetsService } from './timesheets.service';
import { UpdateService } from './update.service';
import { UsersService } from './users.service';
import { WorkMachinesService } from './work-machines.service';
import { WorkerTicketsService } from './worker-tickets.service';

@Injectable({
  providedIn: 'root',
})
export class PlusService {
  private clientsService = inject(ClientsService);
  private contractorsService = inject(ContractorsService);
  private initializeService = inject(InitializeService);
  private licenseService = inject(LicenseService);
  private materialsService = inject(MaterialsService);
  private modalCtrl = inject(ModalController);
  private navigationService = inject(NavigationService);
  private notesService = inject(NotesService);
  private partnerNotesService = inject(PartnerNotesService);
  private popoverController = inject(PopoverController);
  private sitesService = inject(SitesService);
  private siteAreasService = inject(SiteAreasService);
  private siteProjectsService = inject(SiteProjectsService);
  private siteReportsService = inject(SiteReportsService);
  private timesheetsService = inject(TimesheetsService);
  private schedulesService = inject(SchedulesService);
  private translate = inject(TranslateService);
  private updateService = inject(UpdateService);
  private usersService = inject(UsersService);
  private workMachinesService = inject(WorkMachinesService);
  private workerTicketsService = inject(WorkerTicketsService);

  isLoading = signal(false);
  readonly isIos = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

  inviteUser(options?: { role?: Roles; site?: string | null }) {
    if (this.licenseService.isFreeLicenseS()) {
      if (this.isIos) {
        return;
      }
      this.modalCtrl.create({ component: FreeSelectWorkerDialogComponent }).then((m) => {
        m.present();
      });
    } else {
      this.modalCtrl.create({
        component: InviteUserDialogComponent,
        componentProps: {
          role: options?.role ?? null,
          site: options?.site ?? null,
        },
      }).then((m) => {
        m.present();

        m.onDidDismiss<User>().then((data) => {
          if (data.data) {
            this.isLoading.set(true);
            this.usersService.invite(data.data).pipe(
              take(1),
            ).subscribe((value) => {
              this.isLoading.set(false);
              if (value.result) {
                this.initializeService.updateUsers().pipe(
                  take(1),
                ).subscribe((updated) => {
                  if (updated && this.usersService.role() !== Roles.superAdmin) {
                    this.navigationService.navigateToUser(value.user);
                  }
                });
              } else {
                Dialog.alert({
                  title: this.translate.instant('users.creation'),
                  message: this.translate.instant('users.creationFailed'),
                });
              }
            });
          }
        });
      });
    }
  }

  async onNewTask(sites?: Site[], project?: SiteProject, job?: SiteProjectPeriod, setWorker = false,
    skipToast = false, date: Date = null, user: User = null, cssClass?: string[],
    additionalWork?: boolean, observationCategory?: 'safety' | 'design-control') {
    if (sites == null || sites.length > 1 || sites.length === 0) {
      return this.workerTicketsService.openSiteTicketDialog({
        site: null,
        project,
        job,
        sites,
        setWorker,
        skipToast,
        date,
        user,
        cssClass,
        additionalWork,
        observationCategory,
      });
    }
    const site = sites[0];
    return this.workerTicketsService.openSiteTicketDialog({ site, project, job, sites, setWorker, skipToast, date, user, cssClass, additionalWork, observationCategory });
  }

  onNewContract(site: Site) {
    this.modalCtrl.create({ component: SiteContractDialogComponent, componentProps: { site }, cssClass: ['workspace-modal'] }).then((m) => {
      m.present();

      m.onDidDismiss<SiteProject>().then((data) => {
        if (data.data) {
          data.data.site = site.guid;
          data.data.client = site.client;
          console.log(data.data);
        }
      });
    });
  }

  onNewReport(site: Site) {
    this.modalCtrl.create({ component: NewSiteReportDialogComponent, componentProps: { site }, cssClass: ['workspace-modal'] }).then((m) => {
      m.present();
    });
  }

  async onNewNote(site?: Site) {
    let selectedSite = site;
    if (!selectedSite) {
      const sites = this.sitesService.getListSync();
      const selectModal = await this.modalCtrl.create({
        component: SelectSiteDialog,
        componentProps: { sites, isDisabledToggle: true },
      });
      await selectModal.present();
      const selectedData = await selectModal.onDidDismiss<Site>();
      selectedSite = selectedData.data ?? null;
      if (!selectedSite) {
        return;
      }
    }
    const m = await this.modalCtrl.create({
      component: NewNoteDialogComponent,
      componentProps: { site: selectedSite },
      cssClass: ['workspace-modal'],
    });
    m.present();
    const data = await m.onDidDismiss<Note>();
    if (data.data) {
      data.data.site = selectedSite.guid;
      data.data.sitename = selectedSite.name;
      this.notesService.save(data.data);
    }
  }

  async onNewRound(site?: Site, inputSelectedRound: string = null) {
    let selectedSite = site;
    if (!selectedSite) {
      const sites = this.sitesService.getListSync();
      const selectModal = await this.modalCtrl.create({
        component: SelectSiteDialog,
        componentProps: { sites, isDisabledToggle: true },
      });
      await selectModal.present();
      const selectedData = await selectModal.onDidDismiss<Site>();
      selectedSite = selectedData.data ?? null;
      if (!selectedSite) {
        return;
      }
    }

    this.modalCtrl.create({
      component: SalesRoundDialogComponent,
      componentProps: { site: selectedSite, inputSelectedRound },
      cssClass: ['shell-modal', 'stack-modal'],
      backdropDismiss: false,
    }).then((m) => {
      m.present();

      m.onDidDismiss<{ roomTickets: RoomTicket[]; type: string; url: string }>().then(async (data) => {
        if (!data.data) {
          return;
        }

        const createdTickets = await this.workerTicketsService.saveRoomTicketsPromise(data.data.roomTickets, null, true);
        const tickets = createdTickets.map((it) => ({
          guid: it.guid,
          parentGuid: null,
          name: it.name,
        } as SiteReportTicket));
        if (data.data.type === 'inspect') {
          const contractor = this.contractorsService.contractorS();
          const user = this.usersService.currentUserS();
          const item: SiteReport = {
            contractor: { guid: contractor.guid, name: contractor.name },
            created: Timestamp.now(),
            creator: { guid: user.guid, name: user.displayName },
            site: selectedSite.guid,
            url: data.data.url,
            name: 'inspect',
            tickets,
          };
          this.siteReportsService.save(item);
        }
      });
    });
  }

  async onNewAttachment(site?: Site, project?: SiteProject | null): Promise<Attachment[] | undefined> {
    let selectedSite = site;
    if (!selectedSite) {
      const sites = this.sitesService.getListSync();
      const selectModal = await this.modalCtrl.create({
        component: SelectSiteDialog,
        componentProps: { sites, isDisabledToggle: true },
      });
      await selectModal.present();
      const selectedData = await selectModal.onDidDismiss<Site>();
      selectedSite = selectedData.data ?? null;
      if (!selectedSite) {
        return undefined;
      }
    }

    const modal = await this.modalCtrl.create({
      component: AddAttachmentDialogComponent,
      componentProps: { document: 'attachment', site: selectedSite, project: project ?? null },
      cssClass: ['shell-modal', 'stack-modal'],
    });

    await modal.present();
    const data = await modal.onDidDismiss<IAttachment[]>();
    if (!data.data?.length) {
      return undefined;
    }

    const contractor = this.contractorsService.contractorS()?.guid ?? null;
    return data.data.map((item) => Object.assign({}, item, {
      site: selectedSite.guid,
      project: project?.guid ?? null,
      contractors: item.contractors.length > 0 ? item.contractors : contractor != null ? [contractor] : [],
      showToClient: contractor == null ? true : item.showToClient,
    }));
  }

  onInstruction(event: Event) {
    this.popoverController.create({ component: InstructionPopover, event }).then((p) => {
      p.present();
    });
  }

  onNewClient() {
    this.modalCtrl
      .create({ component: NewClientDialogComponent })
      .then((m) => {
        m.present();
        m.onDidDismiss<Client>().then((data) => {
          if (data.data) {
            this.createClient(data.data).catch((error) => {
              console.error('manual client create failed', error);
            });
          }
        });
      });
  }

  async createClient(client: Client) {
    const payload = this.sanitizeClientPayload(client);
    await this.clientsService.saveOrThrow(payload);

    const contractor = this.contractorsService.contractorS();
    if (contractor) {
      const contractorClients = contractor.clients ?? [];
      const nextClients = Array.from(new Set([...contractorClients, payload.guid]));
      contractor.clients = nextClients;
      await this.contractorsService.updateOnly(contractor.guid, { clients: nextClients });
    }

    await firstValueFrom(this.initializeService.updateClients().pipe(take(1)));
  }

  private sanitizeClientPayload(client: Client): Client {
    const optionalStringFields = [
      'email',
      'phone',
      'businessId',
      'streetAddress',
      'address',
      'postalCode',
      'city',
      'description',
      'contactName',
      'contactEmail',
      'contactPhone',
    ] as const;

    const payload = {
      ...client,
      guid: (client.guid ?? '').trim(),
      name: (client.name ?? '').trim(),
      type: client.type ?? 'company',
    } as Client & Record<string, unknown>;

    const sanitized = {} as Client & Record<string, unknown>;
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined) {
        sanitized[key] = value;
      }
    });

    optionalStringFields.forEach((field) => {
      const value = sanitized[field];
      if (value == null) {
        return;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
          sanitized[field] = null;
        } else {
          sanitized[field] = trimmed;
        }
      }
    });

    const finalPayload = {} as Client & Record<string, unknown>;
    Object.entries(sanitized).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        finalPayload[key] = value;
      }
    });

    return finalPayload;
  }

  async onNewSite(clientGuid: string) {
    return this.modalCtrl
      .create({ component: NewSiteDialogComponent, componentProps: { clientGuid } })
      .then(async (m) => {
        m.present();
        return m.onDidDismiss<Site>().then(async (data) => {
          if (data.data) {
            return this.createSite(data.data);
          }
        });
      });
  }

  async createSite(site: Site): Promise<Site> {
    const payload = this.sanitizeSitePayload({ ...site });
    delete payload.file;
    const result = await firstValueFrom(this.sitesService.provision(payload));
    payload.guid = result.site;

    const contractor = this.contractorsService.contractorS();
    if (contractor) {
      // Add default project to site.
      this.siteProjectsService.createDefaultProject(payload, contractor.guid);
      this.initializeService.update();
    }

    return payload;
  }

  private sanitizeSitePayload(site: Site): Site {
    const payload = { ...site } as Site & Record<string, unknown>;
    const optionalStringFields = ['client', 'image', 'worknumber'] as const;

    optionalStringFields.forEach((field) => {
      const value = payload[field];
      if (value == null) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete payload[field];
        return;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete payload[field];
        } else {
          payload[field] = trimmed;
        }
      }
    });

    if (payload.additionalInfo?.notes?.trim().length === 0) {
      delete payload.additionalInfo;
    }

    Object.keys(payload).forEach((key) => {
      const typedKey = key as keyof Site;
      if (payload[typedKey] === undefined || payload[typedKey] === null) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete payload[typedKey];
      }
    });

    return payload as Site;
  }

  async onNewTimesheet(site?: Site, user?: User, date?: Date, newTimesheet = false) {
    const currentUser = this.usersService.currentUserS();
    if (newTimesheet || currentUser.role !== Roles.worker) {
      this.modalCtrl
        .create({ component: NewTimesheetDialogComponent, componentProps: { site, user, date } })
        .then((m) => {
          m.present();
          m.onDidDismiss().then((data) => {
            if (data.data) {
              const timesheet = data.data;
              this.timesheetsService.save(timesheet, true).then(() => this.updateService.update());
            }
          });
        });
    } else {
      let selectedSite = site ?? null;
      const selectedDate = date ?? new Date();
      const dateString = selectedDate.toLocaleDateString('fi');

      if (!selectedSite) {
        const contractor = currentUser.contractors?.[0] ?? null;
        const dayTimesheets = await firstValueFrom(this.timesheetsService.getList({
          user: currentUser.guid,
          startDate: selectedDate,
          endDate: selectedDate,
          contractor,
        }).pipe(
          take(1),
        ));
        const openTimesheet = dayTimesheets.find((item) => item.dateOut == null);
        const fallbackTimesheet = openTimesheet ?? dayTimesheets[0] ?? null;
        if (fallbackTimesheet?.site) {
          selectedSite = this.sitesService.getSync(fallbackTimesheet.site) ?? null;
        }
      }

      if (!selectedSite) {
        const schedule = await firstValueFrom(this.schedulesService.getSingleOnce({
          date: dateString,
          user: currentUser.guid,
        }).pipe(
          take(1),
        ));
        if (schedule?.site) {
          selectedSite = this.sitesService.getSync(schedule.site) ?? null;
        }
      }

      if (!selectedSite) {
        const selectModal = await this.modalCtrl.create({
          component: SelectSiteDialog,
          componentProps: { sites: this.sitesService.getListSync(), isDisabledToggle: true },
        });
        await selectModal.present();
        const selected = await selectModal.onDidDismiss<Site>();
        selectedSite = selected.data ?? null;
      }

      if (!selectedSite) {
        return;
      }

      this.modalCtrl.create({
        component: SiteTimesheetDialogComponent,
        componentProps: { site: selectedSite },
      }).then(async (m) => m.present());
    }
  }

  onNewPartnerNote(status?: string) {
    this.modalCtrl
      .create({ component: PartnerNoteDialogComponent, componentProps: { status } })
      .then((m) => {
        m.present();
        m.onDidDismiss<PartnerNote>().then((data) => {
          if (data.data) {
            data.data.inviter = this.usersService.currentUserS().guid;
            this.partnerNotesService.save(data.data);
          }
        });
      });
  }

  newProduct() {
    this.modalCtrl.create({ component: NewMaterialDialog }).then((m) => {
      m.present();

      m.onDidDismiss<ListItem>().then((data) => {
        if (data.data) {
          this.materialsService.save(data.data);
        }
      });
    });
  }

  onWorkMachine() {
    this.modalCtrl.create({ component: WorkMachineDialogComponent }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.workMachinesService.save(data.data);
        }
      });
    });
  }

  private deleteChild(item: SiteArea, id: string) {
    if (item.children.length === 0) {
      return item.id !== id ? item : null;
    }

    item.children = item.children.map((c) => this.deleteChild(c, id)).filter((c) => c != null);
    return item;
  }

  onAddAreas(site: Site, parent?: SiteArea, root?: SiteArea) {
    this.modalCtrl.create({
      component: AddAreaDialogComponent,
      componentProps: { parent, root },
      backdropDismiss: false,
      cssClass: ['stack-modal'],
    }).then((m) => {
      m.present();

      m.onDidDismiss<{
        areas?: string[];
        rooms?: Room[];
        delete?: boolean;
        name?: string;
        areaDefaults?: Pick<SiteArea, 'size' | 'volume' | 'height' | 'internal_doors' | 'external_doors' | 'windows'>;
      }>().then((data) => {
        if (data.data) {
          if (data.data.rooms) {
            parent.rooms = data.data.rooms;
            this.siteAreasService.update(root);
            return;
          }
          if (data.data.delete) {
            if (root.id === parent.id) {
              this.siteAreasService.delete(root);
            } else {
              root.children = root.children.map((child) => this.deleteChild(child, parent.id));
              this.siteAreasService.update(root);
            }
            return;
          }
          if (data.data.areas) {
            const existing = parent?.children?.map((it) => it) ?? [];
            if (parent) {
              parent.children = [];
            }
            if (data.data.areas.length === 0) {
              if (parent?.guid) {
                parent.name = data.data.name;
                this.siteAreasService.update(parent);
              } else {
                this.siteAreasService.update(root);
              }
            } else {
              data.data.areas.forEach((areaName) => {
                const id = parent ? `${parent.id}${parent.children.length}` : '0';
                const existingArea = existing?.find((it) => it.id === id);
                const area: SiteArea = {
                  id,
                  name: areaName,
                  site: site.guid,
                  rooms: existingArea?.rooms ?? [],
                  children: existingArea?.children ?? [],
                  size: existingArea?.size ?? null,
                  volume: existingArea?.volume ?? null,
                  height: existingArea?.height ?? null,
                  internal_doors: existingArea?.internal_doors ?? null,
                  external_doors: existingArea?.external_doors ?? null,
                  windows: existingArea?.windows ?? null,
                };
                const defaults = data.data.areaDefaults;
                if (defaults) {
                  const fields: (keyof typeof defaults)[] = ['size', 'volume', 'height', 'internal_doors', 'external_doors', 'windows'];
                  fields.forEach((field) => {
                    if (defaults[field] != null) {
                      area[field] = defaults[field] ?? null;
                    }
                  });
                }
                if (parent) {
                  parent.children.push(area);
                } else {
                  this.siteAreasService.save(area);
                }
              });
              if (parent) {
                if (parent.guid) {
                  parent.name = data.data.name;
                  this.siteAreasService.update(parent);
                } else {
                  this.siteAreasService.update(root);
                }
              }
            }
          }
        }
      });
    });
  }
}
